import { useCallback } from 'react';
import { isBefore, isSameDay } from 'date-fns';
import { isDayDisabled } from '../utils';

export const useCalendarHandlers = (
    isRangeSelection: boolean,
    rangeStart: Date | null,
    setRangeStart: (date: Date | null) => void,
    rangeEnd: Date | null,
    setRangeEnd: (date: Date | null) => void,
    localSelectedDate: Date,
    setLocalSelectedDate: (date: Date) => void,
    setHoverDate: (date: Date | null) => void,
    minDate: Date,
    maxDate: Date,
    disabledDays: Date[],
    onChange: (date: Date) => void,
    onRangeChange?: (start: Date, end: Date) => void,
    closeCalendar: () => void
) => {
    // Handle clicking on a date in the calendar
    const handleDateClick = useCallback((day: Date) => {
        if (isDayDisabled(day, disabledDays, minDate, maxDate)) return;

        if (isRangeSelection) {
            if (!rangeStart || (rangeStart && rangeEnd)) {
                setRangeStart(day);
                setRangeEnd(null);
                setLocalSelectedDate(day);
                if (onRangeChange) onRangeChange(day, day);
            } else {
                let newStart = rangeStart;
                let newEnd = day;

                if (isBefore(day, rangeStart)) {
                    newStart = day;
                    newEnd = rangeStart;
                }

                setRangeStart(newStart);
                setRangeEnd(newEnd);
                setLocalSelectedDate(newStart);
                if (onRangeChange) onRangeChange(newStart, newEnd);
            }
        } else {
            setLocalSelectedDate(day);
            onChange(day);
            closeCalendar();
        }
    }, [
        isRangeSelection, minDate, maxDate, disabledDays,
        rangeStart, rangeEnd, onChange, onRangeChange,
        setRangeStart, setRangeEnd, setLocalSelectedDate, closeCalendar
    ]);

    // Handle mouse enter on a day cell (for range selection preview)
    const handleMouseEnter = useCallback((day: Date) => {
        if (isRangeSelection && rangeStart && !rangeEnd) {
            setHoverDate(day);
        }
    }, [isRangeSelection, rangeStart, rangeEnd, setHoverDate]);

    // Handle mouse leave from a day cell
    const handleMouseLeave = useCallback(() => {
        setHoverDate(null);
    }, [setHoverDate]);

    // Confirm the selected range
    const confirmSelection = useCallback(() => {
        if (rangeStart && rangeEnd) {
            onChange(rangeStart);
            closeCalendar();
        }
    }, [rangeStart, rangeEnd, onChange, closeCalendar]);

    // Cancel the selection process
    const cancelSelection = useCallback((startDateProp?: Date, endDateProp?: Date) => {
        if (startDateProp && endDateProp) {
            setRangeStart(startDateProp);
            setRangeEnd(endDateProp);
        } else {
            setRangeStart(null);
            setRangeEnd(null);
        }
        closeCalendar();
    }, [setRangeStart, setRangeEnd, closeCalendar]);

    return {
        handleDateClick,
        handleMouseEnter,
        handleMouseLeave,
        confirmSelection,
        cancelSelection
    };
};